import './bootstrap';
import '../css/app.css';

import { createApp, h } from 'vue';
import { createInertiaApp } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ZiggyVue } from 'ziggy-vue';
import route from 'ziggy';
import { i18nVue, loadLanguageAsync } from 'laravel-vue-i18n';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
	faBell,
	faEdit,
	faExclamationCircle,
	faCheckCircle,
	faUserShield,
	faTrashCan,
	faCircle,
	faPeopleGroup,
	faChartColumn,
	faCheckSquare,
	faBan,
	faFilePdf,
	faPlus,
	faEye,
	faUser,
	faEnvelope,
	faPhone,
	faHome,
	faEarth,
	faEuroSign,
	faHatWizard,
	faBolt,
	faComputer,
	faReply,
	faFileZipper,
	faRotate,
	faChevronDown,
	faClock,
	faMoneyBillTrendUp,
	faArrowTrendDown,
	faArrowTrendUp,
	faCaretDown,
	faCaretUp,
	faBox,
	faFileContract,
	faPaperclip,
	faFile,
	faFileExcel,
	faFileWord,
	faFilePowerpoint,
	faLink,
	faMoneyBill,
	faCrown,
	faQuestionCircle,
	faList,
	faInfoCircle,
	faBuildingColumns,
	faGear,
	faArrowRightArrowLeft,
	faLinkSlash,
	faTriangleExclamation,
	faSpinner,
	faChevronLeft,
	faChevronRight,
	faPen,
	faChartBar,
	faXmark,
	faTrash,
	faListCheck,
} from '@fortawesome/free-solid-svg-icons';
import {
	faGoogle,
	faJira,
	faLinkedin,
	faSlack,
} from '@fortawesome/free-brands-svg-icons';

/* Tailwind Datepicker */
import VueTailwindDatepicker from 'vue-tailwind-datepicker';

import Vue3ColorPicker from 'vue3-colorpicker';
import 'vue3-colorpicker/style.css';
import { faCalendar } from '@fortawesome/free-regular-svg-icons';

library.add(
	faBell,
	faEdit,
	faExclamationCircle,
	faCheckCircle,
	faUserShield,
	faTrashCan,
	faCircle,
	faPeopleGroup,
	faSlack,
	faGoogle,
	faJira,
	faChartColumn,
	faCheckCircle,
	faCheckSquare,
	faBan,
	faFilePdf,
	faPlus,
	faEye,
	faUser,
	faEnvelope,
	faPhone,
	faHome,
	faEarth,
	faLinkedin,
	faEuroSign,
	faHatWizard,
	faBolt,
	faCalendar,
	faComputer,
	faReply,
	faFileZipper,
	faRotate,
	faChevronDown,
	faClock,
	faMoneyBillTrendUp,
	faArrowTrendUp,
	faArrowTrendDown,
	faCaretDown,
	faCaretUp,
	faBox,
	faFileContract,
	faPaperclip,
	faFile,
	faFileExcel,
	faFileWord,
	faFilePowerpoint,
	faLink,
	faMoneyBill,
	faCrown,
	faQuestionCircle,
	faList,
	faInfoCircle,
	faBuildingColumns,
	faGear,
	faArrowRightArrowLeft,
	faLinkSlash,
	faTriangleExclamation,
	faSpinner,
	faChevronLeft,
	faChevronRight,
	faPen,
	faChartBar,
	faXmark,
	faTrash,
	faListCheck,
);

import { settings } from '@cleos/plugins/settings';
import { appHeader } from '@cleos/plugins/app-header';
import filters from './filters';

const langs = import.meta.glob('../../lang/*.json');

const appName =
	window.document.getElementsByTagName('title')[0]?.innerText || 'Cleos';

createInertiaApp({
	title: (title) =>
		title !== appName ? (title ? `${appName} | ${title}` : appName) : title,
	resolve: async (name) => {
		const fromPages = () =>
			resolvePageComponent(
				`./pages/${name}.page.vue`,
				import.meta.glob('./pages/**/*.page.vue'),
			);

		const fromModules = () =>
			resolvePageComponent(
				`./modules/${name}.page.vue`,
				import.meta.glob('./modules/**/*.page.vue'),
			);

		// Pages published from Composer packages (e.g. jagaad/laravel-banking)
		const fromVendors = () =>
			resolvePageComponent(
				`./vendor/${name}.page.vue`,
				import.meta.glob('./vendor/**/*.page.vue'),
			);

		const results = await Promise.allSettled([
			fromModules(),
			fromVendors(),
			fromPages(),
		]);

		const resolved = results.find((result) => result.status === 'fulfilled');

		if (!resolved) {
			throw new Error(
				`404: Page not Found — component "${name}" not found in pages/ or modules/`,
			);
		}

		return resolved.value;
	},
	setup({ el, App, props, plugin }) {
		const locale = props.initialPage?.props?.locale ?? 'en';
		const application = createApp({ render: () => h(App, props) })
			.use(plugin)
			.use(settings)
			.use(appHeader)
			.use(ZiggyVue)
			.use(VueTailwindDatepicker)
			.use(Vue3ColorPicker)
			.use(i18nVue, {
				lang: locale,
				fallbackMissingTranslations: true,
				// Lazy: only the active locale (and the fallback) is downloaded,
				// not every language pack.
				resolve: (lang) => {
					const load = langs[`../../lang/${lang}.json`];
					return load ? load() : Promise.resolve({ default: {} });
				},
			})
			.component('FontAwesomeIcon', FontAwesomeIcon)
			.mixin({
				methods: {
					route: route,
				},
			});

		const log = function (...data) {
			if (import.meta.env.VITE_APP_DEBUG === 'true') {
				console.log(...data);
			}
		};
		application.provide('log', log);

		application.config.globalProperties.$filters = filters;

		// Mount once the active language is loaded, so keys never flash.
		loadLanguageAsync(locale).finally(() => application.mount(el));
	},
	progress: {
		color: '#009FE3',
	},
});
